import React, { memo, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  CustomEventDto,
  GraphConfig,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import { getGridWidthCalc } from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";
interface CustomMetricsAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}
export const CustomMetricsAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: CustomMetricsAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);

    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const onMetricDetailsClick = () => {};
    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        <Grid>
          <BaseMetricSection
            groupTitle={"Custom Metrics"}
            showCompareGroups={true}
          >
            {graphConfigs.map((config, index) => {
              // const isDataLoading = data
              return (
                <Grid key={index} size={gridWidth}>
                  <BaseGraphPaper>
                    <GraphsWrapper
                      // isDataLoading={true}
                      isDataLoading={data[0].isLoading || data[0].isPending}
                      totalTestVisitors={totalExperienceVisitors}
                      config={config}
                      onMetricDetailsClick={onMetricDetailsClick}
                      testDuration={testDuration}
                      renderLocation={"experiment"}
                      data={selectData({
                        dashboardData: data,
                        config,
                        _resource: experience,
                        locale: storeCurrencyConfig?.value?.locale,
                        metricFilterIndex: 0,
                        variations,
                      })}
                      compareVariationId={compareVariationId}
                      rawData={data}
                    />
                  </BaseGraphPaper>
                </Grid>
              );
            })}
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={graphConfigs}
            dropdownOptions={graphConfigs.map((config) => ({
              title: config.title[0],
              id: config.impactMetric ? config.impactMetric : config.metrics[0],
            }))}
            variationOverviewData={data}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            graphConfigs={graphConfigs}
            testDuration={testDuration}
          />
        </Grid>
      </Stack>
    );
  }
);
